import React from "react";
import { defineMessages, useIntl } from "react-intl";

import { HStack, Stack, Text } from "@/components/ui";
import { formatLiveStatusLabel } from "@/features/sports/formatters";
import Signal from "@/features/ui/components/signal";
import { useTheme } from "@/hooks";
import { cn } from "@/utils/cn";

import type { SportData, Team } from "@/schemas";

const messages = defineMessages({
  live: { id: "common.live", defaultMessage: "Live" },
  final: { id: "common.final", defaultMessage: "Final" },
  home: { id: "common.home", defaultMessage: "Home" },
  away: { id: "common.away", defaultMessage: "Away" },
});

type TextAlign = "left" | "right" | "center";

interface LiveIndicatorProps {
  data: SportData;
  /** "compact" omits the "Live" label, "full" includes it (used by primary). */
  size?: "compact" | "full";
  textSize?: "xs" | "md";
  iconSpacing?: 1 | 2;
}

const LiveIndicator = ({
  data,
  size = "compact",
  textSize = "xs",
  iconSpacing = 1,
}: LiveIndicatorProps) => {
  const intl = useIntl();
  return (
    <HStack
      alignItems="center"
      justifyContent="center"
      space={iconSpacing}
      className="text-accent-pink-500"
    >
      <Signal />
      {size === "full" && (
        <Text size={textSize} weight="medium" theme="inherit">
          {intl.formatMessage(messages.live)}
        </Text>
      )}
      <Text size={textSize} weight="medium" theme="inherit">
        {formatLiveStatusLabel(data)}
      </Text>
    </HStack>
  );
};

const FinalText = () => {
  const intl = useIntl();
  return (
    <Text size="xs" theme="muted">
      {intl.formatMessage(messages.final)}
    </Text>
  );
};

interface GameStatusProps {
  data: SportData;
  /** Rendered when the game is neither live nor over (e.g. upcoming time). */
  fallback?: React.ReactNode;
}

/** Picks the right status display based on live/over flags. */
const GameStatus = ({ data, fallback = null }: GameStatusProps) => {
  if (data.is_live) return <LiveIndicator data={data} />;
  if (data.is_over) return <FinalText />;
  return <>{fallback}</>;
};

interface TeamScoreProps {
  value: number | null | undefined;
  align?: TextAlign;
  className?: string;
}

const TeamScore = ({ value, align, className }: TeamScoreProps) => (
  <Text
    size="2xl"
    weight="bold"
    align={align}
    className={cn("tabular-nums", className)}
  >
    {value}
  </Text>
);

interface HomeAwayLabelProps {
  side: "home" | "away";
  size?: "xs" | "sm";
  align?: TextAlign;
  className?: string;
}

const HomeAwayLabel = ({
  side,
  size = "xs",
  align,
  className,
}: HomeAwayLabelProps) => {
  const intl = useIntl();
  return (
    <Text size={size} theme="muted" align={align} className={className}>
      {intl.formatMessage(side === "home" ? messages.home : messages.away)}
    </Text>
  );
};

interface UpcomingTimeProps {
  startAt: string;
}

const UpcomingTime = ({ startAt }: UpcomingTimeProps) => {
  const intl = useIntl();
  return (
    <Stack alignItems="center" justifyContent="center">
      <Text size="xs">
        {intl.formatTime(startAt, { timeZoneName: "short" })}
      </Text>
      <Text size="xs" theme="muted">
        {intl.formatDate(new Date(startAt), {
          month: "short",
          day: "numeric",
        })}
      </Text>
    </Stack>
  );
};

interface LeagueBadgeProps {
  league: string;
}

const LeagueBadge = ({ league }: LeagueBadgeProps) => (
  <div className="flex h-8 min-w-8 shrink-0 items-center justify-center rounded-full border bg-background px-1">
    <Text size="xs" weight="bold" className="text-[10px]!">
      {league}
    </Text>
  </div>
);

interface TeamAvatarProps {
  team: Team;
  size?: "md" | "lg";
}

const TeamAvatar = ({ team, size = "md" }: TeamAvatarProps) => {
  const theme = useTheme();
  const hasLogo = Boolean(team.logo_url);
  const colorKey = theme === "dark" ? "dark_background" : "light_background";
  const primaryColor = team.icon_background_colors[0]?.[colorKey] ?? "4B5563";
  const secondaryColor =
    team.icon_background_colors[1]?.[colorKey] ?? primaryColor;

  return (
    <div
      className={cn("flex items-center justify-center", {
        "size-10": size === "md",
        "size-14": size === "lg",
        "rounded-xl text-xs font-semibold text-white": !hasLogo,
      })}
      style={
        hasLogo
          ? undefined
          : {
              background: `linear-gradient(0deg, #${primaryColor}, #${secondaryColor})`,
            }
      }
      aria-hidden
    >
      {team.logo_url ? (
        <img
          src={team.logo_url}
          alt={team.name}
          className="size-full rounded-xl object-contain"
        />
      ) : (
        team.abbreviation.slice(0, 3)
      )}
    </div>
  );
};

interface ScoreLineProps {
  homeScore: number | null | undefined;
  awayScore: number | null | undefined;
  className?: string;
}

/**
 * The "homeScore - awayScore" block used in Primary and Secondary (mobile center).
 */
const ScoreLine = ({ homeScore, awayScore, className }: ScoreLineProps) => (
  <div
    className={cn(
      "grid grid-cols-[1fr_auto_1fr] items-center gap-3",
      className,
    )}
  >
    <TeamScore value={homeScore} align="right" />
    <Text size="md" weight="bold">
      -
    </Text>
    <TeamScore value={awayScore} align="left" />
  </div>
);

export {
  FinalText,
  GameStatus,
  HomeAwayLabel,
  LiveIndicator,
  TeamScore,
  UpcomingTime,
  LeagueBadge,
  TeamAvatar,
  ScoreLine,
};
